import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, X, CheckCircle, AlertCircle, Loader2, ArrowRight, Plus } from "lucide-react";
import { API } from "../App";

const STAGE = { DROP: "drop", PROCESSING: "processing", DONE: "done" };

export default function UploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const [stage, setStage] = useState(STAGE.DROP);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState([]);

  const fetchSources = useCallback(async () => {
    const token = localStorage.getItem("taxbuddy_token");
    try {
      const res = await axios.get(`${API}/api/upload/sources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSources(res.data.sources || []);
    } catch {}
  }, []);

  const handleDeleteAll = async () => {
    if (!window.confirm("Delete ALL statements and transactions? This cannot be undone.")) return;
    const token = localStorage.getItem("taxbuddy_token");
    try {
      await axios.delete(`${API}/api/transactions/all/clear`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSources([]);
      toast.success("All statements and transactions deleted.");
    } catch {
      toast.error("Failed to delete. Please try again.");
    }
  };

  useEffect(() => {
    if (stage === STAGE.DROP) fetchSources();
  }, [stage, fetchSources]);

  const addFiles = useCallback((newFiles) => {
    const pdfs = Array.from(newFiles).filter(f => f.name.toLowerCase().endsWith(".pdf"));
    if (!pdfs.length) { setError("Please select PDF files only."); return; }
    setError(null);
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      const unique = pdfs.filter(f => !existing.has(f.name + f.size));
      return [...prev, ...unique];
    });
  }, []);

  const removeFile = (index) => setFiles(prev => prev.filter((_, i) => i !== index));

  const handleDrop = (e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); };

  const handleProcess = async () => {
    if (!files.length) return;
    setStage(STAGE.PROCESSING);
    setError(null);

    const token = localStorage.getItem("taxbuddy_token");
    const formData = new FormData();
    files.forEach(f => formData.append("files", f));

    try {
      const res = await axios.post(`${API}/api/upload/multiple`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 300000, // 5 minutes — no more timeouts
      });
      setResult(res.data);
      setStage(STAGE.DONE);
      toast.success(`Done! ${res.data.total_inserted ?? res.data.inserted ?? "?"} transactions imported.`);
    } catch (err) {
      const msg = err.code === "ECONNABORTED"
        ? "Upload timed out. Try uploading fewer files at once (5 max)."
        : err.response?.data?.detail || "Upload failed. Please try again.";
      setError(msg);
      setStage(STAGE.DROP);
      toast.error(msg);
    }
  };

  // ── DROP STAGE ──────────────────────────────────────────────────────────────
  if (stage === STAGE.DROP) return (
    <div style={{ maxWidth: 640, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: 0 }}>Upload Statements</h1>
        <p style={{ fontSize: 13, color: "#6B7280", marginTop: 6 }}>
          Upload up to 5 PDF bank statements at a time. Works with ING, CommBank, Bendigo, Westpac, ANZ, NAB and more.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? "#1A4D2E" : "#D1D5DB"}`,
          borderRadius: 16,
          padding: "40px 24px",
          textAlign: "center",
          cursor: "pointer",
          background: dragging ? "#F0FDF4" : "#FAFAFA",
          transition: "all 0.2s",
          marginBottom: 20,
        }}
      >
        <Upload size={32} color="#9CA3AF" style={{ marginBottom: 12 }} />
        <p style={{ fontWeight: 700, color: "#374151", marginBottom: 4 }}>Drop PDF statements here</p>
        <p style={{ fontSize: 13, color: "#9CA3AF" }}>or click to browse</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          style={{ display: "none" }}
          onChange={e => addFiles(e.target.files)}
        />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div style={{ marginBottom: 20, display: "flex", flexDirection: "column", gap: 8 }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: "10px 14px" }}>
              <FileText size={16} color="#1A4D2E" />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              <span style={{ fontSize: 12, color: "#9CA3AF", marginRight: 8 }}>{(f.size / 1024).toFixed(0)} KB</span>
              <button onClick={e => { e.stopPropagation(); removeFile(i); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 2 }}>
                <X size={14} />
              </button>
            </div>
          ))}
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px dashed #D1D5DB", borderRadius: 10, padding: "8px 14px", fontSize: 13, color: "#6B7280", cursor: "pointer", width: "fit-content" }}
          >
            <Plus size={14} /> Add more files
          </button>
        </div>
      )}

      {error && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "10px 14px", marginBottom: 16 }}>
          <AlertCircle size={15} color="#DC2626" />
          <span style={{ fontSize: 13, color: "#DC2626" }}>{error}</span>
        </div>
      )}

      <button
        onClick={handleProcess}
        disabled={files.length === 0}
        style={{
          width: "100%", padding: "14px", background: files.length ? "#1A4D2E" : "#D1D5DB",
          color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700,
          cursor: files.length ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}
      >
        <Upload size={18} /> Upload {files.length > 0 ? `${files.length} file${files.length > 1 ? "s" : ""}` : "statements"}
      </button>

      <p style={{ fontSize: 12, color: "#9CA3AF", textAlign: "center", marginTop: 12 }}>
        Max 5 files per upload · PDF only · Your data is private and secure
      </p>

      {/* Previously uploaded statements */}
      {sources.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: "#374151", margin: 0 }}>
              Statements uploaded ({sources.length})
            </p>
            <button
              onClick={handleDeleteAll}
              style={{ fontSize: 12, color: "#DC2626", background: "none", border: "1px solid #FECACA", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}
            >
              Delete all
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {sources.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: "10px 14px" }}>
                <FileText size={14} color="#6B7280" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "#111827", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.filename}
                  </p>
                  <p style={{ fontSize: 11, color: "#9CA3AF", margin: 0 }}>
                    {s.bank} · {s.transaction_count} transactions · {s.period}
                  </p>
                </div>
                <p style={{ fontSize: 11, color: "#9CA3AF", flexShrink: 0 }}>
                  {s.uploaded_at ? new Date(s.uploaded_at).toLocaleDateString("en-AU") : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ── PROCESSING STAGE ────────────────────────────────────────────────────────
  if (stage === STAGE.PROCESSING) return (
    <div style={{ maxWidth: 480, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", textAlign: "center", padding: "60px 24px" }}>
      <Loader2 size={48} color="#1A4D2E" style={{ animation: "spin 1s linear infinite", marginBottom: 24 }} />
      <h2 style={{ fontSize: 20, fontWeight: 800, color: "#111827", marginBottom: 8 }}>Processing your statements…</h2>
      <p style={{ fontSize: 14, color: "#6B7280", marginBottom: 4 }}>
        Reading {files.length} file{files.length > 1 ? "s" : ""}. This can take up to 2 minutes.
      </p>
      <p style={{ fontSize: 13, color: "#9CA3AF" }}>Please don't close this tab.</p>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // ── DONE STAGE ──────────────────────────────────────────────────────────────
  if (stage === STAGE.DONE && result) {
    const inserted = result.total_inserted ?? result.inserted ?? 0;
    const duplicates = result.total_duplicates ?? result.duplicates ?? 0;
    const fileResults = result.file_results || [];

    return (
      <div style={{ maxWidth: 580, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
        <div style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 16, padding: 24, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <CheckCircle size={22} color="#059669" />
            <h2 style={{ fontSize: 18, fontWeight: 800, color: "#065F46", margin: 0 }}>Upload complete</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div style={{ background: "#fff", borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#059669" }}>{inserted}</div>
              <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>transactions imported</div>
            </div>
            <div style={{ background: "#fff", borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#9CA3AF" }}>{duplicates}</div>
              <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>duplicates skipped</div>
            </div>
          </div>

          {fileResults.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
              {fileResults.map((fr, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  {fr.inserted > 0
                    ? <CheckCircle size={14} color="#059669" />
                    : <AlertCircle size={14} color="#F59E0B" />}
                  <span style={{ flex: 1, color: "#374151" }}>{fr.filename}</span>
                  <span style={{ color: "#6B7280" }}>{fr.inserted} imported</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={() => { setFiles([]); setResult(null); setStage(STAGE.DROP); }}
            style={{ flex: 1, padding: "12px", background: "#fff", color: "#1A4D2E", border: "1px solid #1A4D2E", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            Upload more
          </button>
          <button
            onClick={() => navigate("/transactions")}
            style={{ flex: 1, padding: "12px", background: "#1A4D2E", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            Review transactions <ArrowRight size={15} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
